import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ErrorSnackBarData,
  NormalSnackBarData,
  SnackBarService,
} from '@qtek/libs/snack-bar-core';
import { isNil } from '@qtek/shared/utils';
import {
  catchError,
  filter,
  from,
  map,
  mergeMap,
  of,
  takeUntil,
  tap,
} from 'rxjs';
import { TasksActions } from './tasks.actions';
import { TasksService } from './tasks.service';

@Injectable()
export class TasksEffects {
  private actions$ = inject(Actions);
  private tasksService = inject(TasksService);
  private snackBarService = inject(SnackBarService);

  createTask$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TasksActions.createTask),
        tap(({ payload, mysid }) => {
          this.tasksService.createTask(payload, mysid);
          this.showTaskMessage({
            type: 'NormalMessage',
            message: 'GNR_TASK_CREATED_SUCCESSFULLY',
          });
        })
      ),
    { dispatch: false }
  );

  updateTask$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TasksActions.updateTask),
        tap(({ payload, mysid, id }) => {
          this.tasksService.updateTask(payload, mysid, id);
          this.showTaskMessage({
            type: 'NormalMessage',
            message: 'GNR_TASK_UPDATED_SUCCESSFULLY',
          });
        })
      ),
    { dispatch: false }
  );

  deleteTasks$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TasksActions.deleteTasks),
        tap(({ taskIds, mysid }) => {
          this.tasksService.deleteTasks(taskIds, mysid);
          this.showTaskMessage({
            type: 'NormalMessage',
            message: 'GNR_TASK_DELETED_SUCCESSFULLY',
          });
        })
      ),
    { dispatch: false }
  );

  subscribeTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TasksActions.subscribeTasks),
      mergeMap(action => {
        const { payload, mysid } = action;

        return this.tasksService.subscribe(payload as any, mysid).pipe(
          map(message => ({ ...message, subscriptionMysid: mysid })),
          takeUntil(
            this.actions$.pipe(
              ofType(TasksActions.unsubscribeTasks),
              filter(unsubscribeAction => {
                return unsubscribeAction.mysid === mysid;
              })
            )
          )
        );
      }),
      filter((data: any) => data.op),
      mergeMap(({ op, res, sts, subscriptionMysid }: any) => {
        switch (op) {
          case 'query':
            if (res || !isNil(sts?.meta)) {
              const actions: any[] = [
                TasksActions.loadTasksSuccess({
                  tasks: res || [],
                  mysid: subscriptionMysid,
                }),
              ];
              if (sts?.meta) {
                actions.push(
                  TasksActions.loadTasksMetaSuccess({
                    meta: sts.meta,
                    mysid: subscriptionMysid,
                  })
                );
              }

              return from(actions);
            } else {
              return of(
                TasksActions.loadTasksFailure({
                  error: new Error(
                    sts?.message || 'GNR_FAILED_TO_SUBSCRIBE_TASKS'
                  ),
                  mysid: subscriptionMysid,
                })
              );
            }

          case 'ins':
            if (res) {
              return of(
                TasksActions.createTaskSuccess({
                  task: res,
                  mysid: subscriptionMysid,
                })
              );
            } else {
              this.showTaskMessage({
                type: 'ErrorMessage',
                message: 'GNR_TASK_CREATE_FAILED',
              });
              return of(
                TasksActions.createTaskFailure({
                  error: new Error(sts?.message || 'GNR_TASK_CREATE_FAILED'),
                  mysid: subscriptionMysid,
                })
              );
            }

          case 'upd':
          case 'upds':
            if (res) {
              return of(
                TasksActions.updateTaskSuccess({
                  task: res,
                  mysid: subscriptionMysid,
                })
              );
            } else {
              this.showTaskMessage({
                type: 'ErrorMessage',
                message: 'GNR_TASK_UPDATE_FAILED',
              });
              return of(
                TasksActions.updateTaskFailure({
                  error: new Error(sts?.message || 'GNR_TASK_UPDATE_FAILED'),
                })
              );
            }

          case 'del':
            if (res) {
              return of(
                TasksActions.deleteTaskSuccess({
                  task: res,
                  mysid: subscriptionMysid,
                })
              );
            } else {
              this.showTaskMessage({
                type: 'ErrorMessage',
                message: 'GNR_TASK_DELETE_FAILED',
              });
              return of(
                TasksActions.deleteTaskFailure({
                  error: new Error(sts?.message || 'GNR_TASK_DELETE_FAILED'),
                })
              );
            }

          default:
            return of({ type: 'NOT_EXIST' });
        }
      }),
      catchError(error =>
        of(TasksActions.loadTasksFailure({ error, mysid: '' }))
      )
    )
  );

  subscribeSingleTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TasksActions.subscribeSingleTask),
      mergeMap(action => {
        const { taskId } = action;

        return this.tasksService
          .subscribeSingleTask(taskId)
          .pipe(
            takeUntil(
              this.actions$.pipe(ofType(TasksActions.unsubscribeSingleTask))
            )
          );
      }),
      filter((data: any) => data.op),
      mergeMap(({ op, res, sts, id }: any) => {
        switch (op) {
          case 'get':
            if (res) {
              return of(
                TasksActions.loadSingleTaskSuccess({
                  task: res,
                })
              );
            } else {
              return of(
                TasksActions.loadSingleTaskFailure({
                  error: new Error(sts?.message || 'GNR_FAILED_GET_TASK'),
                })
              );
            }

          default:
            return of({ type: 'NOT_EXIST' });
        }
      }),
      catchError(error => {
        return of(
          TasksActions.loadSingleTaskFailure({
            error,
          })
        );
      })
    )
  );

  getDealsForTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TasksActions.getDealsForTask),
      mergeMap(() =>
        this.tasksService.getDealsForTask().pipe(
          map(response => {
            if (response?.res) {
              return TasksActions.getDealsForTaskSuccess({
                deals: response.res,
              });
            } else {
              return TasksActions.getDealsForTaskFailure({
                error: new Error('Failed to get deals for task'),
              });
            }
          }),
          catchError(error => {
            return of(
              TasksActions.getDealsForTaskFailure({
                error,
              })
            );
          })
        )
      )
    )
  );

  private showTaskMessage(data: NormalSnackBarData | ErrorSnackBarData): void {
    this.snackBarService.showSnackBar(data);
  }
}
